import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, GraduationCap, Users, MessageSquare, FileText, Bell, Award, ClipboardCheck, Home as HomeIcon, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

const employeeLinks = [
  { to: "/academy", label: "Academy", icon: LayoutDashboard },
  { to: "/clock", label: "Time Clock", icon: Clock },
  { to: "/academy/training", label: "Training Center", icon: GraduationCap },
  { to: "/academy/certificates", label: "Certificates", icon: Award },
];

const familyLinks = [
  { key: "fam-dash", to: "/family", label: "Dashboard", icon: LayoutDashboard },
  { key: "fam-intake", to: "/family?tab=intake", label: "Intake", icon: FileText },
  { key: "fam-eval", to: "/family?tab=evaluation", label: "Evaluations", icon: ClipboardCheck },
  { key: "fam-back", to: "/", label: "Back to site", icon: HomeIcon },
];

const adminLinks = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/clock", label: "Time Clock", icon: Clock },
  { to: "/admin/employees", label: "Employees", icon: Users },
  { to: "/admin/families", label: "Families", icon: Users },
  { to: "/admin/referrals", label: "Referrals", icon: FileText, badgeKey: "referrals" },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, badgeKey: "messages" },
];

export default function PortalShell({ children, variant }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const links = variant === "admin" ? adminLinks : variant === "family" ? familyLinks : employeeLinks;
  const brandLabel = variant === "admin" ? "Admin Console" : variant === "family" ? "Family Portal" : "WeCare II Academy";

  const [counts, setCounts] = useState({ messages: 0, referrals: 0, applications: 0, total: 0 });

  useEffect(() => {
    if (variant !== "admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/unread-counts");
        if (!cancelled) setCounts(data);
      } catch (e) {
        // Non-blocking: badges are nice-to-have, keep zeros rather than crashing the sidebar.
        if (e?.response?.status !== 401) {
          console.warn("Failed to fetch admin unread counts:", e?.message || e);
        }
      }
    };
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => { cancelled = true; clearInterval(t); };
  }, [variant, location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`portal-${variant}`}>
      <aside className="fixed inset-y-0 left-0 w-64 hidden lg:flex flex-col bg-white border-r border-slate-100 px-6 py-8">
        <Link to="/" className="mb-2" data-testid="portal-brand"><BrandLogo className="h-8"/></Link>
        <div className="text-xs uppercase tracking-[0.16em] text-slate-400 font-semibold mt-1">{brandLabel}</div>

        <nav className="mt-10 space-y-1">
          {links.map(({ key, to, label, icon: Icon, badgeKey }) => {
            const badge = badgeKey ? counts[badgeKey] || 0 : 0;
            return (
              <NavLink
                key={key || to}
                to={to}
                end
                data-testid={`portal-nav-${label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${isActive ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-50 hover:text-brand-navy"}`
                }
                style={{ transition: "background-color 200ms ease, color 200ms ease" }}
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4"/>
                    <span className="flex-1">{label}</span>
                    {badge > 0 && (
                      <span
                        data-testid={`portal-badge-${badgeKey}`}
                        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold leading-none ${isActive ? "bg-white text-brand-orange" : "bg-brand-orange text-white"}`}
                      >
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto">
          <div className="rounded-2xl border border-slate-100 p-4">
            <div className="text-xs text-slate-500">Signed in as</div>
            <div className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="portal-user-name">{user?.name}</div>
            <div className="text-xs text-slate-500">{user?.email}</div>
            <button data-testid="portal-logout-btn" onClick={async () => { await logout(); nav("/"); }}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 hover:bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700">
              <LogOut className="w-3.5 h-3.5"/> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-100 px-6 py-3 flex items-center justify-between">
        <Link to="/"><BrandLogo className="h-7"/></Link>
        <button data-testid="portal-mobile-logout" onClick={async () => { await logout(); nav("/"); }} className="text-slate-600 text-sm inline-flex items-center gap-1">
          <LogOut className="w-4 h-4"/> Sign out
        </button>
      </div>

      <main className="lg:pl-64">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">{children}</div>
      </main>
    </div>
  );
}
